
import { InputBaseComponentProps, TextFieldProps } from '@mui/material';
import { DatePicker, DateValidationError, PickerChangeHandlerContext } from '@mui/x-date-pickers';
import { useFieldActions } from '@ngt/forms-core';
import dayjs, { Dayjs } from 'dayjs';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';

import { InputDatePickerProps } from './types/FieldTypes';

export interface IInputDateFieldProps extends InputDatePickerProps { 
    errors?: string[];
    hasErrors?: boolean;
    fullWidth?: boolean;
    format?: string;
    inputProps?: InputBaseComponentProps;
    renderProps?: TextFieldProps;
}

const InputDateField: FC<IInputDateFieldProps> = ({
    FieldInputProps: { state: { value }, actions: { update, blur, focus, touch } },
    fullWidth,
    onChange,
    sx,
    hasErrors,
    errors,
    slotProps,
    format,
    slots,
    ...rest
}) => {
    const [_, setOpen] = useState(false);
    
    const { setErrors } = useFieldActions();

    const derivedValue = useMemo(() => {
        if (!value) {
            return null;
        }

        return dayjs(value);
    }, [value]);

    const [state, setState] = useState<Dayjs | null>(derivedValue);

    useEffect(() => {
        if (derivedValue !== null && state === null){
            setState(derivedValue);
        }
    }, [state, derivedValue]);

    const onChangeCombined = useCallback((date: Dayjs | null, context: PickerChangeHandlerContext<DateValidationError>) => {
        if (onChange) {
            onChange(date, context);
        }

        setState(date);

        if (!date?.isValid()) { 
            return;
        }

        update(date?.toISOString() as any, true, true, true);
    }, [onChange, update, setErrors, setState]);

    const textFieldProps: Partial<TextFieldProps> = slotProps?.textField as any;

    const onFocusCombined = useCallback((event: React.FocusEvent<HTMLTextAreaElement | HTMLInputElement>) => {
        textFieldProps?.onFocus?.(event);

        focus();
    }, [focus, textFieldProps?.onFocus]);

    const onBlurCombined = useCallback((event: React.FocusEvent<HTMLTextAreaElement | HTMLInputElement>) => {
        textFieldProps?.onBlur?.(event);

        if (!state?.isValid()) {
            update(undefined, true, true, false);
            setState(null);
        }

        blur();
        touch();
    }, [blur, touch, state, setState, textFieldProps?.onBlur]);

    const onOpen = useCallback(() => {
        setOpen(true);
        focus();
    }, [setOpen, focus]);

    const onClose = useCallback(() => {
        setOpen(false);
        //blur();
    }, [setOpen, blur]);

    return (
        <DatePicker
            {...rest as any}
            format={format === undefined ? 'dd/MM/yyyy' : format}
            onOpen={onOpen}
            onClose={onClose}
            value={state}
            onChange={onChangeCombined}
            slotProps={{
                ...slotProps,
                textField: {
                    ...textFieldProps,
                    size: 'small',
                    fullWidth: textFieldProps?.fullWidth ?? (fullWidth === undefined ? true : fullWidth),
                    onBlur: onBlurCombined,
                    onFocus: onFocusCombined,
                    sx: {...textFieldProps?.sx },
                    error: hasErrors,
                    helperText: errors ? 
                        errors.map(x => (<div>{x}</div>)) :
                        undefined
                }
            }}
            slots={slots}
        />
    );
}

export default InputDateField